import React, { useState, useRef, useEffect } from "react";
import * as THREE from "three";
import { Html } from "@react-three/drei";
import { Popover, ActionIcon, Group, Button, MantineProvider, Grid, Divider, Text, Textarea } from '@mantine/core';
import { useField } from '@mantine/form';

import { MobileScreenShareOutlined } from "@mui/icons-material";

import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import SendIcon from '@mui/icons-material/Send';
import PanToolIcon from '@mui/icons-material/PanTool';
import DeleteIcon from '@mui/icons-material/Delete';

import { MoveCommentButton } from "./AddCommentButton"
import { getComments } from '../api'
import { useObjectStore } from "../zustand/objects";

const InSceneComment = ({ id, position, artboard, currentObjectArtboard }) => {

  const [hover1, setHover1] = useState(false);
  const [hover2, setHover2] = useState(false);
  const [open, setOpen] = useState(false)

  const messagesEndRef = useRef(null); // Reference to the bottom of the scrollable div
  const popoverContentRef = useRef(null); // Ref for the Popover.Dropdown content
  const targetElementRef = useRef(null); // Ref for the Popover.Target div

  const { addComment, deleteThread, loadThreads, viewedComments, viewedThread, setViewedComments, setViewedThread, scrubViewedComments } = useObjectStore()

  const handleDelete = () => {
    deleteThread(id)
    setTimeout(() => {
      loadThreads()
    }, "500");
  }


  const handlePopOpen = (passedId) => {
    setOpen(true)
    setViewedThread(passedId)
    loadComments()
    setTimeout(() => {
      open && viewedThread === passedId && viewedComments.length > 0 && messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, 200);
  }

  const handlePopClose = () => {
    setOpen(false)
    scrubViewedComments()
    setViewedThread(null)
  }

  const loadComments = () => {
    getComments(id)
      .then(res => setViewedComments(res))
    setTimeout(() => {
      open && viewedThread === id && viewedComments.length > 0 && messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }

  useEffect(() => {
    viewedThread === id && setOpen(true)
  }, [viewedThread])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverContentRef.current &&
        !popoverContentRef.current.contains(event.target) &&
        targetElementRef.current &&
        !targetElementRef.current.contains(event.target) &&
        viewedThread === id
      ) {
        handlePopClose();
      }
    };

    open && document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);


  return (
    <Html
      position={new THREE.Vector3(position.x, position.y, position.z)}
      as='div' // Wrapping element (default: 'div')
      wrapperClass // The className of the wrapping element (default: undefined)
      prepend // Project content behind the canvas (default: false)
      fullscreen // Aligns to the upper-left corner, fills the screen (default:false) [ignored in transform mode]
      distanceFactor={5} // If set (default: undefined), children will be scaled by this factor, and also by distance to a PerspectiveCamera / zoom by a OrthographicCamera.
      zIndexRange={[100, 0]} // Z-order range (default=[16777271, 0])
      transform // If true, applies matrix3d transformations (default=false)
      sprite // Renders as sprite, but only in transform mode (default=false)
    >
      <MantineProvider>
        <Popover
          closeOnClickOutside
          opened={viewedThread === id}
          // disabled={artboard != currentObjectArtboard} 
          onOpen={() => handlePopOpen(id)}
          onClose={handlePopClose}
          width={300} trapFocus position="bottom" withArrow shadow="md">
          <Popover.Target>
            <div
              ref={targetElementRef}
              onClick={() => setViewedThread(id)}
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #fac710, #fac721)", // Vibrant gradient background
                borderRadius: "50%", // Circular shape
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", // Subtle shadow
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s", // Smooth hover animation
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.2)")
              } // Scale up on hover
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              } // Scale back on hover out
            >
              {/* <CommentOutlinedIcon
                style={{ fontSize: "24px", color: "white" }} // Icon styling
              /> */}
              💬
            </div>
          </Popover.Target>
          <Popover.Dropdown
            ref={popoverContentRef}
            pl={10}
            pr={10}
            pt={0}
            style={{ overflowX: "none" }}
          >
            <Grid>
              <Grid.Col span={12} offset={8}>
                <Group>
                  <MoveCommentButton threadsId={id} artboard={artboard} position={position} />
                  <ActionIcon ml={5} w={30} h={40} variant="transparent" size={'xs'}
                    onClick={() => handleDelete()}
                    onPointerOver={() => setHover2(true)}
                    onPointerLeave={() => setHover2(false)}
                    color={!hover2 ? "#424242" : "#868e96"} >
                    <DeleteIcon fontSize='medium' /></ActionIcon>
                </Group></Grid.Col>
              <Grid.Col span={12} pt={0}><Divider /></Grid.Col>
            </Grid>
            <Grid pt={10} style={{
              minHeight: "20px", // Set max height
              maxHeight: "200px", // Set max height
              overflowY: "auto",  // Enable vertical scrolling
              overflowX: "hidden"
            }}>
              {viewedThread === id && viewedComments.map((mapped, i) => (
                <Grid.Col span={12} key={i} >
                  <UserComment id={mapped.id} threadId={id} userNameProp={mapped.author} commentDateProp={new Date(mapped.created_at).toLocaleDateString()} fullTextProp={mapped.comment_content} loadComments={loadComments} />
                </Grid.Col>))}
              <div ref={messagesEndRef} />
            </Grid>
            <Grid>
              <Grid.Col span={12}>
                <Grid.Col span={12} pt={0}><Divider /></Grid.Col>
                <CommentSubmit id={id} loadComments={loadComments} addComment={addComment} />
              </Grid.Col></Grid>
          </Popover.Dropdown>
        </Popover>
      </MantineProvider>
    </Html>
  )
};

export default InSceneComment;

const CommentSubmit = ({ id, loadComments, addComment }) => {
  const [hover1, setHover1] = useState(false)

  const { currentObjectArtboard, threads } = useObjectStore()

  const handleCommentSubmit = (val) => {
    addComment(id, val, currentObjectArtboard) 
  }

  const field = useField({
    initialValue: '',
    validateOnBlur: true,
    validate: (value) => (value.trim().length < 1 ? 'Please enter a value' : handleCommentSubmit(value)),
  });

  return (
    <div
      style={{
        position: "sticky",
        bottom: 0,
        background: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "none",
        overflowX: "none"
      }}
    >
      <Textarea
        {...field.getInputProps()}
        placeholder="Leave a reply..."
        style={{
          border: "none",
          outline: "none",
          flex: 1, // Make the textarea take available space
          fontSize: "16px", // Larger placeholder text
          color: "#7c7c7c",
          paddingBottom: "10px", // Increased padding for vertical centering
          paddingLeft: "10px", // Increased padding for vertical centering
          paddingRight: "10px", // Increased padding for vertical centering
          lineHeight: "1.5", // Adjust line height for better vertical alignment
          overflow: "hidden", // Prevent overflow
        }}
      />

      {/* Send Icon */}
      <ActionIcon ml={5} w={30} h={40} variant="transparent" size={'xs'}
        onPointerOver={() => setHover1(true)}
        onPointerLeave={() => setHover1(false)}
        onClick={() => field.validate}
        color={!hover1 ? "#424242" : "#868e96"} >
        <SendIcon /></ActionIcon>
    </div>
  )
}


const UserComment = ({ id, threadId, userNameProp, commentDateProp, fullTextProp, loadComments }) => {

  const { deleteComment } = useObjectStore()

  const [hover, setHover] = useState(false)

  const handleDelete = () => {
    deleteComment(threadId, id)
    // setTimeout(() => {
    //   loadComments()
    // }, "500");  
  }

  return (
    <Grid style={{
      paddingBottom: 0
    }}>
      <Grid.Col pb={0} span={5}><div style={{ paddingLeft: 10, paddingTop: 10, paddingBottom: 0 }}>
        <Text size="xs" fw={500}>{userNameProp}</Text></div></Grid.Col>
      <Grid.Col pb={0} span={5}><div style={{ paddingRight: 10, paddingTop: 10, paddingBottom: 0 }}>
        <Text size="xs">{commentDateProp}</Text></div></Grid.Col>
      <Grid.Col pb={0} span={2}>
        <ActionIcon ml={5} w={30} h={40} variant="transparent" size={'xs'} onClick={() => handleDelete()}
          onPointerOver={() => setHover(true)}
          onPointerLeave={() => setHover(false)}
          color={!hover ? "#424242" : "#868e96"} >
          <DeleteIcon fontSize='small' /></ActionIcon></Grid.Col>
      <Grid.Col span={12} pt={0}><div style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 0 }}>
        <Text size="xs">{fullTextProp}</Text></div></Grid.Col>
    </Grid>
  )
} 